import React, { useState, useEffect } from 'react';
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, BarElement, ArcElement, Title, Tooltip, Legend, Filler } from 'chart.js';
import { Line, Bar, Doughnut } from 'react-chartjs-2';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, ArcElement, Title, Tooltip, Legend, Filler);

const KpiCard = ({ icon, label, value, change, changeType, sub, gradient }) => (
  <div className="kpi-card">
    <div className="kpi-glow" style={{ background: gradient }} />
    <div className="kpi-icon" style={{ background: gradient }}>{icon}</div>
    <div className="kpi-content">
      <div className="kpi-label">{label}</div>
      <div className="kpi-value">{value}</div>
      {change !== undefined && (
        <div className={`kpi-change ${changeType}`}>
          {changeType === 'positive' ? '↑' : changeType === 'negative' ? '↓' : '→'} {Math.abs(change)}%
        </div>
      )}
      {sub && <div className="kpi-sub">{sub}</div>}
    </div>
  </div>
);

const CHART_OPTS_BASE = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      position: 'bottom',
      labels: { color: '#9CA3AF', font: { size: 12 }, boxWidth: 12, padding: 16 }
    },
    tooltip: {
      backgroundColor: 'rgba(17,17,24,0.95)',
      titleColor: '#E5E7EB',
      bodyColor: '#9CA3AF',
      borderColor: 'rgba(255,255,255,0.1)',
      borderWidth: 1
    }
  },
  scales: {
    x: { grid: { color: 'rgba(255,255,255,0.04)' }, ticks: { color: '#6B7280', font: { size: 11 } } },
    y: { beginAtZero: true, grid: { color: 'rgba(255,255,255,0.04)' }, ticks: { color: '#6B7280', font: { size: 11 } } }
  }
};

// apiBase/token: yonetim panelindeki AI Center bu bilesenleri DEGISTIRMEDEN
// yeniden kullanabilsin diye opsiyonel - verilmezse tenant'taki mevcut
// davranis (kendi crm_token'i + /api/callops) AYNEN korunur.
const DashboardTab = ({ apiBase = '/api/callops', token } = {}) => {
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('7d');
  const authToken = token || localStorage.getItem('crm_token');

  useEffect(() => { fetchDashboard(); }, [period]);

  const fetchDashboard = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${apiBase}/dashboard?period=${period}`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      const data = await res.json();
      setDashboardData(data);
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const stats = dashboardData?.stats || {};
  const callVolume = dashboardData?.callVolume || [];
  const sectorData = dashboardData?.sectorPerformance || [];

  const callVolumeChartData = {
    labels: callVolume.map(c => c.date),
    datasets: [
      { label: 'Gelen', data: callVolume.map(c => c.inbound), borderColor: '#60A5FA', backgroundColor: 'rgba(96,165,250,0.08)', fill: true, tension: 0.4, borderWidth: 2, pointBackgroundColor: '#60A5FA', pointRadius: 3 },
      { label: 'Giden', data: callVolume.map(c => c.outbound), borderColor: '#34D399', backgroundColor: 'rgba(52,211,153,0.08)', fill: true, tension: 0.4, borderWidth: 2, pointBackgroundColor: '#34D399', pointRadius: 3 },
      { label: 'Cevapsız', data: callVolume.map(c => c.missed), borderColor: '#F87171', backgroundColor: 'rgba(248,113,113,0.08)', fill: true, tension: 0.4, borderWidth: 2, pointBackgroundColor: '#F87171', pointRadius: 3 }
    ]
  };

  const sectorChartData = {
    labels: sectorData.map(s => s.sector),
    datasets: [{
      label: 'Lead Sayısı',
      data: sectorData.map(s => s.count),
      backgroundColor: ['rgba(96,165,250,0.7)', 'rgba(52,211,153,0.7)', 'rgba(251,191,36,0.7)', 'rgba(248,113,113,0.7)', 'rgba(167,139,250,0.7)', 'rgba(251,146,60,0.7)'],
      borderRadius: 6,
      borderWidth: 0
    }]
  };

  const sentimentChartData = {
    labels: ['Pozitif', 'Nötr', 'Negatif'],
    datasets: [{
      data: [stats.positiveSentiment || 0, stats.neutralSentiment || 0, stats.negativeSentiment || 0],
      backgroundColor: ['rgba(52,211,153,0.8)', 'rgba(251,191,36,0.8)', 'rgba(248,113,113,0.8)'],
      borderWidth: 0,
      hoverOffset: 8
    }]
  };

  return (
    <div className="dashboard-tab">
      {/* Header */}
      <div className="dash-header">
        <div>
          <h2>📊 Genel Bakış</h2>
          <p className="dash-sub">Gerçek zamanlı AI operasyon metrikleri</p>
        </div>
        <div className="period-selector">
          {['24h', '7d', '30d', '90d'].map(p => (
            <button key={p} className={period === p ? 'active' : ''} onClick={() => setPeriod(p)}>
              {p === '24h' ? '24s' : p === '7d' ? '7g' : p === '30d' ? '30g' : '90g'}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="dash-loading">
          <div className="dash-spinner" />
          <span>Veriler yükleniyor...</span>
        </div>
      ) : (
        <>
          {/* KPI Grid */}
          <div className="kpi-grid">
            <KpiCard icon="📞" label="Toplam Arama" value={stats.totalCalls?.toLocaleString() || '0'}
              change={stats.callChange} changeType={stats.callChange > 0 ? 'positive' : 'negative'}
              gradient="linear-gradient(135deg,#1d4ed8,#60A5FA)" />
            <KpiCard icon="💬" label="Chat Mesajları" value={stats.totalMessages?.toLocaleString() || '0'}
              change={stats.messageChange} changeType={stats.messageChange > 0 ? 'positive' : 'negative'}
              gradient="linear-gradient(135deg,#059669,#34D399)" />
            <KpiCard icon="✅" label="Başarı Oranı" value={`${stats.successRate?.toFixed(1) || 0}%`}
              change={stats.successChange} changeType={stats.successChange > 0 ? 'positive' : 'negative'}
              gradient="linear-gradient(135deg,#7c3aed,#a78bfa)" />
            <KpiCard icon="⏱️" label="Ort. Süre" value={formatDuration(stats.avgDuration)}
              change={stats.durationChange} changeType={stats.durationChange < 0 ? 'positive' : 'negative'}
              gradient="linear-gradient(135deg,#d97706,#fbbf24)" />
            <KpiCard icon="🎯" label="Lead Dönüşüm" value={`${stats.conversionRate?.toFixed(1) || 0}%`}
              change={stats.conversionChange} changeType={stats.conversionChange > 0 ? 'positive' : 'negative'}
              gradient="linear-gradient(135deg,#db2777,#f472b6)" />
            <KpiCard icon="🤖" label="Aktif Agent" value={stats.activeAgents || '0'}
              sub={`/ ${stats.totalAgents || 0} toplam`}
              gradient="linear-gradient(135deg,#0f766e,#2dd4bf)" />
          </div>

          {/* Charts */}
          <div className="charts-grid-3">
            <div className="chart-card span-2">
              <h3>📈 Arama Hacmi</h3>
              <div className="chart-wrap">
                <Line data={callVolumeChartData} options={CHART_OPTS_BASE} />
              </div>
            </div>
            <div className="chart-card">
              <h3>🎭 Duygu Dağılımı</h3>
              <div className="chart-wrap doughnut-wrap">
                <Doughnut data={sentimentChartData} options={{
                  ...CHART_OPTS_BASE,
                  scales: undefined,
                  plugins: { ...CHART_OPTS_BASE.plugins, legend: { ...CHART_OPTS_BASE.plugins.legend } }
                }} />
              </div>
            </div>
          </div>

          <div className="chart-card">
            <h3>🏭 Sektör Performansı</h3>
            <div className="chart-wrap" style={{ height: 200 }}>
              <Bar data={sectorChartData} options={{ ...CHART_OPTS_BASE, indexAxis: 'y', plugins: { ...CHART_OPTS_BASE.plugins, legend: { display: false } } }} />
            </div>
          </div>
        </>
      )}

      <style>{`
        .dashboard-tab { display:flex; flex-direction:column; gap:24px; }

        .dash-header { display:flex; justify-content:space-between; align-items:center; }
        .dash-header h2 { margin:0 0 4px; font-size:22px; font-weight:700; color:#F9FAFB; }
        .dash-sub { margin:0; font-size:13px; color:#6B7280; }

        .dash-loading { display:flex; flex-direction:column; align-items:center; justify-content:center; gap:16px; padding:80px; color:#6B7280; font-size:14px; }
        .dash-spinner { width:40px; height:40px; border:3px solid rgba(255,255,255,0.1); border-top-color:#60A5FA; border-radius:50%; animation:spin 0.9s linear infinite; }
        @keyframes spin { to { transform:rotate(360deg); } }

        .period-selector { display:flex; gap:4px; background:rgba(255,255,255,0.04); border:1px solid rgba(255,255,255,0.07); padding:4px; border-radius:12px; }
        .period-selector button { padding:8px 16px; border:none; background:transparent; color:#6B7280; font-weight:500; font-size:13px; cursor:pointer; border-radius:8px; transition:all 0.2s; }
        .period-selector button.active { background:linear-gradient(135deg,#1d4ed8,#60A5FA); color:#fff; box-shadow:0 4px 12px rgba(96,165,250,0.3); }

        .kpi-grid { display:grid; grid-template-columns:repeat(auto-fit, minmax(200px,1fr)); gap:16px; }

        .kpi-card {
          position:relative; overflow:hidden;
          background:rgba(17,17,24,0.7);
          border:1px solid rgba(255,255,255,0.07);
          border-radius:18px; padding:20px;
          display:flex; align-items:center; gap:16px;
          backdrop-filter:blur(16px);
          transition:all 0.25s;
        }
        .kpi-card:hover { transform:translateY(-3px); border-color:rgba(255,255,255,0.12); box-shadow:0 12px 32px rgba(0,0,0,0.3); }
        .kpi-glow { position:absolute; top:-30px; right:-30px; width:100px; height:100px; border-radius:50%; opacity:0.08; filter:blur(20px); pointer-events:none; }
        .kpi-icon { width:52px; height:52px; border-radius:14px; display:flex; align-items:center; justify-content:center; font-size:22px; flex-shrink:0; }
        .kpi-content { flex:1; }
        .kpi-label { font-size:12px; color:#6B7280; margin-bottom:4px; text-transform:uppercase; letter-spacing:0.04em; }
        .kpi-value { font-size:26px; font-weight:800; color:#F9FAFB; line-height:1; margin-bottom:4px; }
        .kpi-change { font-size:12px; font-weight:600; }
        .kpi-change.positive { color:#34D399; }
        .kpi-change.negative { color:#F87171; }
        .kpi-change.neutral { color:#6B7280; }
        .kpi-sub { font-size:12px; color:#6B7280; margin-top:2px; }

        .charts-grid-3 { display:grid; grid-template-columns:2fr 1fr; gap:20px; }
        .chart-card { background:rgba(17,17,24,0.7); border:1px solid rgba(255,255,255,0.07); border-radius:18px; padding:24px; backdrop-filter:blur(16px); }
        .chart-card.span-2 { grid-column:span 2; }
        .chart-card h3 { margin:0 0 20px; font-size:15px; font-weight:600; color:#E5E7EB; }
        .chart-wrap { height:280px; }
        .doughnut-wrap { display:flex; align-items:center; justify-content:center; }

        @media (max-width:900px) {
          .charts-grid-3 { grid-template-columns:1fr; }
          .chart-card.span-2 { grid-column:span 1; }
        }
      `}</style>
    </div>
  );
};

export default DashboardTab;
